import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ArrowLeft, Pencil, Check, X, Image as ImageIcon, FileText,
  PlusCircle, ListTree, History, CheckCircle2, Archive,
} from "lucide-react";
import { getToken, fetchJob, correctJob, formatEuro, fetchMediaObjectUrl, editJobItem, completeJob, archiveJob } from "@/lib/mastroAuth";
import { StatusBadge } from "@/components/StatusBadge";
import { JOBS } from "@/constants/testIds";

// Job identity = client_name (H1) + job_reference (subtitle).
// Single canonical representation with safe legacy fallback.
const jobDisplayName = (job) =>
  job.client_name || job.client || job.title || job.description || "Lavoro";

const MediaThumb = ({ media }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let created = null;
    if (media.has_binary) {
      fetchMediaObjectUrl(getToken(), media.id)
        .then((u) => {
          if (!active) return;
          created = u;
          setUrl(u);
        })
        .catch(() => active && setFailed(true));
    }
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [media.id, media.has_binary]);

  const label = media.caption || media.filename || media.media_type || "Foto";

  if (media.has_binary && url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${JOBS.media}-open-${media.id}`}
        className="group block w-28"
        title={label}
      >
        <img
          src={url}
          alt={label}
          data-testid={`${JOBS.media}-thumb-${media.id}`}
          className="h-28 w-28 rounded-lg border border-neutral-200 object-cover transition-opacity group-hover:opacity-90"
        />
        {media.caption && (
          <span className="mt-1 block truncate text-xs text-neutral-500">{media.caption}</span>
        )}
      </a>
    );
  }

  return (
    <div className="flex h-28 w-28 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-neutral-200 bg-white px-2 text-center">
      <ImageIcon className="h-5 w-5 text-neutral-300" />
      <span className="truncate text-xs text-neutral-400">
        {failed ? "Non disponibile" : label}
      </span>
    </div>
  );
};

const ITEM_FIELDS = [
  { key: "description", label: "Descrizione", type: "text" },
  { key: "quantity", label: "Quantità", type: "text" },
  { key: "unit", label: "Unità", type: "text" },
  { key: "unit_price", label: "Prezzo unitario", type: "text" },
];

const JobItemRow = ({ jobId, item, onSaved, editable }) => {
  const [editKey, setEditKey] = useState(null);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const start = (f) => {
    setErr(null);
    setEditKey(f.key);
    setVal(item[f.key] != null ? String(item[f.key]) : "");
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const updated = await editJobItem(getToken(), jobId, item.id, editKey, val);
      onSaved(updated);
      setEditKey(null);
    } catch (e) {
      setErr("Valore non valido.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`${JOBS.items}-row-${item.id}`} className="rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-medium text-neutral-900">{item.description || "—"}</span>
        <span className="text-neutral-500">
          {item.quantity ?? "—"} {item.unit || ""} · <b className="text-neutral-800">{formatEuro(item.line_total)}</b>
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {editable && ITEM_FIELDS.map((f) => (
          <button
            key={f.key}
            data-testid={`${JOBS.items}-edit-${f.key}-${item.id}`}
            onClick={() => start(f)}
            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2 py-1 text-xs font-medium text-neutral-600 transition-colors hover:border-teal-300 hover:text-teal-700"
          >
            <Pencil className="h-3 w-3" /> {f.label}
          </button>
        ))}
      </div>
      {editKey && (
        <div className="mt-2 rounded-md border border-teal-200 bg-teal-50/40 p-2">
          <label className="text-xs font-medium text-neutral-600">
            {ITEM_FIELDS.find((f) => f.key === editKey)?.label}
          </label>
          <div className="mt-1 flex items-center gap-2">
            <input
              data-testid={`${JOBS.items}-input-${item.id}`}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className="flex-1 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
            <button
              data-testid={`${JOBS.items}-save-${item.id}`}
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Salva
            </button>
            <button onClick={() => setEditKey(null)} className="rounded-lg border border-neutral-300 px-2 py-1.5 text-xs text-neutral-600 hover:bg-neutral-50">
              <X className="h-3 w-3" />
            </button>
          </div>
          <p className="mt-1 text-[11px] text-neutral-400">
            Quantità, unità e prezzo modificano il preventivo: verrà creata una nuova versione.
          </p>
          {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
        </div>
      )}
    </div>
  );
};

const AUDIT_LABELS = {
  "job.created": "Lavoro creato", "job.updated": "Lavoro aggiornato",
  "job.selected": "Lavoro selezionato", "jobitem.corrected": "Riga corretta",
  "quote.generated": "Preventivo generato", "variation.proposed": "Extra proposto",
  "variation.confirmed": "Extra confermato", "variation.rejected": "Extra rifiutato",
};

const Section = ({ title, icon: Icon, testid, count, children, isEmpty = false, emptyText }) => (
  <section className={isEmpty ? "mt-4" : "mt-6"} data-testid={testid}>
    <div className="mb-2 flex flex-wrap items-center gap-2">
      {Icon && <Icon className={`h-4 w-4 ${isEmpty ? "text-neutral-300" : "text-neutral-400"}`} />}
      <h2 className={`text-sm font-semibold uppercase tracking-wide ${isEmpty ? "text-neutral-400" : "text-neutral-500"}`}>{title}</h2>
      {count != null && count > 0 && (
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">{count}</span>
      )}
      {isEmpty && emptyText && (
        <span className="text-xs text-neutral-400">· {emptyText}</span>
      )}
    </div>
    {!isEmpty && children}
  </section>
);

const EditableField = ({ label, field, value, jobId, onSaved, editBtnTestId, editable = true }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const updated = await correctJob(getToken(), jobId, field, val);
      onSaved(updated);
      setEditing(false);
    } catch (e) {
      setErr("Impossibile salvare. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase text-neutral-400">{label}</span>
        {!editing && editable && (
          <button data-testid={editBtnTestId} onClick={() => { setVal(value || ""); setEditing(true); }}
            className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline">
            <Pencil className="h-3 w-3" /> Modifica
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-2">
          <textarea data-testid={JOBS.editInput} value={val} onChange={(e) => setVal(e.target.value)} rows={2}
            className="w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20" />
          {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
          <div className="mt-2 flex gap-2">
            <button data-testid={JOBS.editSave} onClick={save} disabled={saving}
              className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-teal-700 disabled:opacity-60">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Salva
            </button>
            <button data-testid={JOBS.editCancel} onClick={() => setEditing(false)}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50">
              <X className="h-3 w-3" /> Annulla
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-1 text-sm text-neutral-800">{value || <span className="text-neutral-400">—</span>}</p>
      )}
    </div>
  );
};

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchJob(getToken(), jobId)
      .then(setData)
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading) {
    return <div className="flex items-center gap-3 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>;
  }
  if (notFound || !data) {
    return (
      <div>
        <button onClick={() => navigate("/app/lavori")} className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-600 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Lavori
        </button>
        <p className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-500">Lavoro non trovato.</p>
      </div>
    );
  }

  const { job, items, media, quote, variations, audit } = data;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid={JOBS.detail}>
      <button data-testid={JOBS.back} onClick={() => navigate("/app/lavori")} className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-600 hover:underline">
        <ArrowLeft className="h-4 w-4" /> Lavori
      </button>

      <div className="flex items-start justify-between gap-3">
        <div data-testid={JOBS.lavoroSummary} className="min-w-0">
          <h1 className="truncate text-2xl font-bold tracking-tight">{jobDisplayName(job)}</h1>
          {job.job_reference && (
            <p className="mt-1 text-sm text-neutral-500">{job.job_reference}</p>
          )}
        </div>
        <StatusBadge status={job.status} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {job.status === "ACTIVE" && (
          <button data-testid={JOBS.complete}
            onClick={async () => { const d = await completeJob(getToken(), jobId); setData(d); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700">
            <CheckCircle2 className="h-4 w-4" /> Termina lavoro
          </button>
        )}
        {job.status !== "ARCHIVED" && (
          <button data-testid={JOBS.archive}
            onClick={async () => { const d = await archiveJob(getToken(), jobId); setData(d); }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50">
            <Archive className="h-4 w-4" /> Archivia
          </button>
        )}
      </div>

      <Section title="Panoramica">
        <EditableField label="Note" field="notes" value={job.notes} jobId={job.id}
          onSaved={setData} editBtnTestId={JOBS.editNotesBtn} editable={job.status === "ACTIVE"} />
      </Section>

      <Section title="Lavorazioni" icon={ListTree} testid={JOBS.items} count={items.length}
        isEmpty={items.length === 0} emptyText="Nessuna lavorazione registrata.">
        <div className="space-y-2">
          {items.map((it) => (
            <JobItemRow key={it.id} jobId={jobId} item={it} onSaved={setData} editable={job.status === "ACTIVE"} />
          ))}
        </div>
      </Section>

      <Section title="Foto" icon={ImageIcon} testid={JOBS.media} count={media.length}
        isEmpty={media.length === 0} emptyText="Nessuna foto associata.">
        <div className="flex flex-wrap gap-3">
          {media.map((m) => (
            <MediaThumb key={m.id} media={m} />
          ))}
        </div>
      </Section>

      <Section title="Preventivo" icon={FileText} testid={JOBS.quote}
        isEmpty={!quote} emptyText="Nessun preventivo collegato.">
        {quote && (
          <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-neutral-500">Stato preventivo</span>
              <StatusBadge status={quote.status} />
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-neutral-500">Imponibile</span><span>{formatEuro(quote.imponibile)}</span>
            </div>
            {quote.vat_applied && (
              <div className="mt-1 flex items-center justify-between">
                <span className="text-neutral-500">IVA {quote.vat_rate ? `${quote.vat_rate}%` : ""}</span>
                <span>{formatEuro(quote.vat_amount)}</span>
              </div>
            )}
            <div className="mt-2 flex items-center justify-between border-t border-neutral-100 pt-2 font-semibold">
              <span>Totale (v{quote.version_number ?? "—"})</span><span>{formatEuro(quote.total)}</span>
            </div>
            <button data-testid={JOBS.quoteLink} onClick={() => navigate(`/app/preventivi/${quote.id}`)}
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-teal-700 hover:underline">
              <FileText className="h-4 w-4" /> Apri preventivo
            </button>
          </div>
        )}
      </Section>

      <Section title="Extra / Varianti" icon={PlusCircle} testid={JOBS.variations} count={variations.length}
        isEmpty={variations.length === 0} emptyText="Nessun extra registrato.">
        <div className="space-y-2">
          {variations.map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm">
              <span className="text-neutral-800">{v.description || "Extra"}</span>
              <div className="flex items-center gap-2">
                <span className="text-neutral-500">{formatEuro(v.line_total)}</span>
                <StatusBadge status={v.status} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Cronologia" icon={History} testid={JOBS.audit} count={audit.length}
        isEmpty={audit.length === 0} emptyText="Nessun evento in cronologia.">
        <ol className="relative space-y-2 border-l border-neutral-200 pl-4">
          {audit.map((a) => (
            <li key={a.id} className="text-sm">
              <span className="absolute -left-1.5 mt-1.5 h-2 w-2 rounded-full bg-teal-500" />
              <span className="font-medium text-neutral-800">{AUDIT_LABELS[a.action] || a.action}</span>
              {a.payload?.field && (
                <span className="text-neutral-500"> · {a.payload.field}</span>
              )}
              <div className="text-xs text-neutral-400">{a.created_at?.replace("T", " ").slice(0, 16)}</div>
            </li>
          ))}
        </ol>
      </Section>
    </motion.div>
  );
}
